<script>
  /* eslint-disable prefer-destructuring */
  import * as R from 'ramda';
  import { now } from '@/_legacy/util/UnixTimestamps';
  import { v4 as uuidv4 } from 'uuid';
  import {
    checkIfAllConditionsAreMet,
    denormalizeInspection,
    normalizeInspection,
    validateInspection,
    validateInspectionItem,
  } from '@/_legacy/services/inspection';
  import encoders from '@/_legacy/store/encoders';
  import LoadingButton from '@/_legacy/components/LoadingButton.vue';
  import ConductInspectionPage from '@/_legacy/components/ConductInspection/ConductInspectionPage.vue';
  import ConductInspectionItem from '@/_legacy/components/ConductInspection/ConductInspectionItem.vue';
  import ConductInspectionModalNotes
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalNotes.vue';
  import ConductInspectionModalPhotos
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalPhotos.vue';
  import ConductInspectionModalIssue
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalIssue.vue';
  import ConductInspectionModalCancel
    from '@/_legacy/components/ConductInspection/ConductInspectionModal/ConductInspectionModalCancel.vue';
  import CloseIcon from '@/_legacy/components/Icons/CloseIcon.vue';
  import ArrowIcon from '@/_legacy/components/Icons/ArrowIcon.vue';
  import { useBuilderMode } from '@/ui/concerns/checklist/useBuilderMode';

  export default {
    name: 'ConductInspectionPreview',
    components: {
      CloseIcon,
      ArrowIcon,
      LoadingButton,
      ConductInspectionPage,
      ConductInspectionItem,
      ConductInspectionModalNotes,
      ConductInspectionModalPhotos,
      ConductInspectionModalIssue,
      ConductInspectionModalCancel,
    },
    props: {
      inspection: {
        type: Object,
        required: true,
      },
      onSubmit: {
        type: Function,
        required: true,
      },
      previewMode: {
        type: [Boolean, String],
        default: false,
      },
    },
    setup() {
      const { isTranslationMode } = useBuilderMode();

      return {
        isTranslationMode,
      };
    },
    data: () => ({
      isCancelModalVisible: false,
      isNotesModalVisible: false,
      isPhotosModalVisible: false,
      isIssueModalVisible: false,
      isLoading: false,
      isFormSubmitted: false,
      flatInspection: null,
      currentPage: null,
      previousPage: null,
    }),
    computed: {
      title() {
        if (this.previewMode === 'tablet') {
          return this.inspection.title;
        }

        return this.isTranslationMode && this.inspection.checklist_title_translated
          ? this.inspection.checklist_title_translated
          : this.inspection.checklist_title;
      },
      description() {
        if (this.previewMode === 'tablet') {
          return this.inspection.description;
        }

        return this.isTranslationMode && this.inspection.checklist_description_translated
          ? this.inspection.checklist_description_translated
          : this.inspection.checklist_description;
      },
      currentPageTitle() {
        return this._pageTitle(this.entities.pages[this.currentPage], this.currentPageIndex + 1);
      },
      nextPageTitle() {
        const index = this.currentPageIndex + 1;
        const uuid = this.pages[index];
        if (!this.entities.pages) {
          return '';
        }
        return this._pageTitle(this.entities.pages[uuid], index + 1);
      },
      previousPageTitle() {
        const index = this.currentPageIndex - 1;
        const uuid = this.pages[index];
        if (!this.entities.pages) {
          return '';
        }
        return this._pageTitle(this.entities.pages[uuid], index + 1);
      },
      subtitle() {
        return this.$t('Page {page} of {total}', {
          page: this.currentPageIndex + 1,
          total: this.pageCount,
        });
      },
      currentItems() {
        return this.entities.pages[this.currentPage] ? this.entities.pages[this.currentPage].items.filter(
          itemUuid => !R.path(
            ['params', 'automatic'],
            this.entities.items[itemUuid],
          ),
        ) : [];
      },
      entities() {
        const inspection = this.flatInspection.entities.inspection[this.flatInspection.result];
        return {
          ...this.flatInspection.entities,
          inspection,
        };
      },
      entityItems() {
        // Compatibility with legacy structure
        return this.entities.inspection.entityItems || this.entities.inspection.entity_items;
      },
      issues() {
        return this.entities.inspection.issues;
      },
      pages() {
        const shouldDisplayHeaderPage = page => {
          const pageHasNoneAutomaticItem = page.items.find(
            uuid => !R.path(
              ['params', 'automatic'],
              this.entities.items[uuid],
            ),
          );
          return (
            pageHasNoneAutomaticItem
            || Object.keys(this.entityItems).length > 1
          );
        };
        return Object.keys(this.entities.pages)
          .filter(uuid => {
            const page = this.entities.pages[uuid];
            if (!page) {
              return false;
            }
            const isHeaderPage = R.path(['params', 'header'], page);
            if (isHeaderPage) {
              return shouldDisplayHeaderPage(page);
            }
            return true;
          }, this);
      },
      pageCount() {
        return this.pages.length;
      },
      currentPageIndex() {
        return this.pages.indexOf(this.currentPage);
      },
      previousPageIndex() {
        return this.pages.indexOf(this.previousPage);
      },
      hasPreviousPage() {
        return this.pages[this.currentPageIndex - 1];
      },
      hasNextPage() {
        return this.pages[this.currentPageIndex + 1];
      },
      enterAnimation() {
        if (this.currentPageIndex < this.previousPageIndex) {
          return 'animated fadeInLeft';
        }
        return 'animated fadeInRight';
      },
      leaveAnimation() {
        if (this.currentPageIndex > this.previousPageIndex) {
          return 'animated fadeOutLeft';
        }
        return 'animated fadeOutRight';
      },
    },
    watch: {
      inspection: {
        deep: true,
        handler(newInspection) {
          this.flatInspection = normalizeInspection(newInspection);
          if (!this.pages.includes(this.currentPage)) {
            this._setUpPagination();
          }
          this.$forceUpdate();
        },
      },
    },
    created() {
      this.flatInspection = normalizeInspection(this.inspection);
      this._startConduction();
      this._setUpPagination();

      if (this.previewMode && R.path(['params', 'header'], this.entities.pages[this.currentPage])) {
        this.goToNextPage();
      }
    },
    methods: {
      entityItemSelected(id) {
        const inspection = this.flatInspection.entities.inspection[this.flatInspection.result];
        this.$set(inspection, 'entityItem', id);
      },
      duplicateSection(sectionUuid) {
        const parent = this._findParentElement(sectionUuid);
        const parentSection = this.flatInspection.entities.items[sectionUuid];
        if (!parent || !parentSection) {
          this.$toasted.show(this.$t('Couldn\'t duplicate the section.'), {
            type: 'error',
          });
          return false;
        }

        const uuid = uuidv4();
        const sectionCount = parentSection.repeated_sections
          ? parentSection.repeated_sections.length + 1
          : 1;
        const clonedSection = {
          ...parentSection,
          uuid,
          title: `${parentSection.title} ${sectionCount}`,
          required: false,
          repeated: true,
          repeat: false,
          repeats: parentSection.uuid,
        };

        this.$set(this.flatInspection.entities.items, uuid, clonedSection);

        // Add cloned section in the repeatable section
        const repeatedSections = !parentSection.repeated_sections
          ? [uuid]
          : [...parentSection.repeated_sections, uuid];
        this.$set(parentSection, 'repeated_sections', repeatedSections);

        // Add cloned section to the correct order in its parent
        const parentItems = parent.items.reduce(
          (acc, itemUuid) => (itemUuid === parentSection.uuid
            ? [...acc, uuid, itemUuid]
            : [...acc, itemUuid]),
          [],
        );
        this.$set(parent, 'items', parentItems);

        return clonedSection;
      },
      deleteSection(uuid) {
        const parent = this._findParentElement(uuid);
        const parentSection = Object.values(
          this.flatInspection.entities.items,
        )
          .find(
            item => item.repeated_sections
              && item.repeated_sections.includes(uuid),
          );
        if (!parent || !parentSection) {
          this.$toasted.show(this.$t('Couldn\'t delete the section.'), {
            type: 'error',
          });
          return false;
        }

        this.$delete(this.flatInspection.entities.items, uuid);

        const parentItems = parent.items.filter(item => item !== uuid);
        this.$set(parent, 'items', parentItems);

        const repeatedSections = parentSection.repeated_sections.filter(
          item => item !== uuid,
        );
        this.$set(parentSection, 'repeated_sections', repeatedSections);

        repeatedSections.forEach((repeatedSectionUuid, index) => {
          const item = this.flatInspection.entities.items[repeatedSectionUuid];
          this.$set(item, 'title', `${parentSection.title} ${index + 1}`);
        });

        return true;
      },
      changeItem({
        uuid,
        response,
        responded,
      }) {
        const item = this.flatInspection.entities.items[uuid];
        this.$set(item, 'response', response);
        this.$set(item, 'responded', responded);

        if (this.isFormSubmitted) {
          const errors = validateInspectionItem(item, this.$t.bind(this));
          if (R.isEmpty(errors)) {
            this.$delete(item, 'errors');
          } else {
            this.$set(item, 'errors', errors);
          }
        }

        if (!item.check_conditions_for) {
          return;
        }

        item.check_conditions_for.forEach(conditionUuid => {
          const conditionalSection = this.flatInspection.entities.items[conditionUuid];
          const allConditionsAreMet = checkIfAllConditionsAreMet(
            conditionalSection,
            item,
          );
          this.$set(
            conditionalSection,
            'allConditionsMet',
            Boolean(allConditionsAreMet),
          );
        });
      },
      cancelModalButtonClicked() {
        this.isCancelModalVisible = false;
        this.$emit('cancel');
      },
      showNotesModal(uuid) {
        this.isNotesModalVisible = this.flatInspection.entities.items[uuid];
      },
      showPhotosModal(uuid) {
        this.isPhotosModalVisible = this.flatInspection.entities.items[uuid];
      },
      showIssueModal(uuid) {
        this.isIssueModalVisible = this.flatInspection.entities.items[uuid];
      },
      saveNotes(notes) {
        const item = this.flatInspection.entities.items[this.isNotesModalVisible.uuid];

        if (notes.length) {
          this.$set(item, 'notes', notes);
        } else {
          this.$delete(item, 'notes');
        }

        this.isNotesModalVisible = false;
      },
      savePhotos(photos) {
        const item = this.flatInspection.entities.items[this.isPhotosModalVisible.uuid];

        if (photos.length) {
          this.$set(item, 'photos', photos);
        } else {
          this.$delete(item, 'photos');
        }

        this.isPhotosModalVisible = false;
      },
      saveIssue(issue) {
        const issues = this.issues || {};
        const inspection = this.flatInspection.entities.inspection[this.flatInspection.result];
        this.$set(inspection, 'issues', {
          ...issues,
          [this.isIssueModalVisible.uuid]: encoders.actionIssue({
            ...issue,
            entityItem: inspection.entity_item,
          }),
        });
      },
      removeIssue() {
        const inspection = this.flatInspection.entities.inspection[this.flatInspection.result];
        this.$delete(inspection.issues, this.isIssueModalVisible.uuid);
      },
      goToPreviousPage() {
        const index = this.currentPageIndex - 1;
        this.$router.push({
          query: {
            ...this.$route.query,
            page: index,
          },
        });
        this.previousPage = this.currentPage;
        this.currentPage = this.pages[index];
      },
      goToNextPage() {
        const index = this.currentPageIndex + 1;
        this.$router.push({
          query: {
            ...this.$route.query,
            page: index,
          },
        });
        this.previousPage = this.currentPage;
        this.currentPage = this.pages[index];
      },
      submit() {
        this.isFormSubmitted = true;

        const entities = this.flatInspection.entities;
        const errors = validateInspection(entities, { $t: this.$t.bind(this) }) || [];

        if (!R.isEmpty(errors)) {
          Object.entries(errors)
            .forEach(([uuid, itemErrors]) => this.$set(
              this.flatInspection.entities.items[uuid],
              'errors',
              itemErrors,
            ));
          return this.$toasted.show(this.$t('There are form errors!'), {
            type: 'error',
          });
        }

        const inspection = entities.inspection[this.flatInspection.result];
        this.$set(inspection, 'conducted_at', now());

        const denormalizedInspection = denormalizeInspection(
          this.flatInspection.result,
          entities,
        );

        this.isLoading = true;
        const submitResult = this.onSubmit(denormalizedInspection);
        this.isLoading = false;
        return submitResult;
      },
      _pageTitle(page, index) {
        if (!page) {
          return '';
        }

        const isHeaderPage = R.path(['params', 'header'], page);

        if (isHeaderPage) {
          return this.$t('Header');
        }

        const pageTitle = this.isTranslationMode && page.title_translated ? page.title_translated : page.title;

        return pageTitle || this.$t('Page {page}', { page: index });
      },
      _startConduction() {
        const inspection = this.flatInspection.entities.inspection[this.flatInspection.result];
        this.$set(inspection, 'started_at', now());

        const entityItems = Object.keys(this.entityItems);

        if (entityItems.length === 1) {
          this.entityItemSelected(entityItems[0]);
        }
      },
      _setUpPagination() {
        const pageIndex = this.$route.query.page || 0;
        this.currentPage = pageIndex < this.pages.length
          ? this.pages[pageIndex]
          : this.pages[0];
        this.previousPage = pageIndex === 0 ? this.currentPage : this.pages[pageIndex - 1];
      },
      _findParentElement(uuid) {
        const findElement = element => element.items && element.items.includes(uuid);

        const page = Object.values(this.flatInspection.entities.pages)
          .find(
            findElement,
          );

        if (page) {
          return this.flatInspection.entities.pages[page.uuid];
        }

        const item = Object.values(this.flatInspection.entities.items)
          .find(
            findElement,
          );

        if (item) {
          return this.flatInspection.entities.items[item.uuid];
        }

        return false;
      },
    },
  };
</script>

<template>
  <div class="preview">
    <div class="preview__header">
      <div class="preview__header-title">
        <button v-if="!previewMode" type="button" @click="isCancelModalVisible = true">
          <CloseIcon />
        </button>

        <div>
          <span :title="title">{{ title }}</span>
          <p v-if="description && !previewMode">
            {{ description }}
          </p>
        </div>
      </div>

      <div class="preview__header-page">
        <span>{{ currentPageTitle }}</span>
        <p>{{ subtitle }}</p>
      </div>
    </div>

    <transition
      :enter-active-class="enterAnimation"
      :leave-active-class="leaveAnimation"
      mode="out-in"
      appear
    >
      <keep-alive>
        <ConductInspectionPage
          :key="currentPage"
          class="preview__page"
          :entities="entities"
          :page="entities.pages[currentPage]"
          @entityItemSelected="entityItemSelected"
        >
          <transition-group name="fade" mode="out-in" appear>
            <ConductInspectionItem
              v-for="item in currentItems"
              :key="item"
              :item="entities.items[item]"
              :entities="entities"
              :preview-mode="previewMode"
              @change="changeItem"
              @duplicateSection="duplicateSection"
              @deleteSection="deleteSection"
              @showNotesModal="showNotesModal"
              @showPhotosModal="showPhotosModal"
              @showIssueModal="showIssueModal"
            />
          </transition-group>

          <LoadingButton
            v-if="!hasNextPage && !previewMode"
            type="button"
            class="btn btn-default btn-primary ml-auto"
            :is-loading="isLoading"
            @click="submit"
          >
            {{ $t('Submit') }}
          </LoadingButton>
        </ConductInspectionPage>
      </keep-alive>
    </transition>

    <div v-if="pageCount > 1" class="preview__footer">
      <button
        v-if="hasPreviousPage"
        :disabled="!hasPreviousPage"
        type="button"
        class="btn"
        @click="goToPreviousPage"
      >
        <ArrowIcon class="mr-3" :rotate="180" />
        <span>{{ previousPageTitle }}</span>
      </button>

      <button
        v-if="hasNextPage"
        :disabled="!hasNextPage"
        class="ml-auto btn"
        type="button"
        @click="goToNextPage"
      >
        <span>{{ nextPageTitle }}</span>
        <ArrowIcon class="ml-3" />
      </button>
    </div>

    <portal to="modals">
      <ConductInspectionModalCancel
        v-if="isCancelModalVisible"
        @confirm="cancelModalButtonClicked"
        @close="isCancelModalVisible = false"
      />

      <ConductInspectionModalNotes
        v-if="isNotesModalVisible"
        :item="isNotesModalVisible"
        @save="saveNotes"
        @close="isNotesModalVisible = false"
      />

      <ConductInspectionModalPhotos
        v-if="isPhotosModalVisible"
        :item="isPhotosModalVisible"
        @save="savePhotos"
        @close="isPhotosModalVisible = false"
      />

      <ConductInspectionModalIssue
        v-if="isIssueModalVisible"
        :item="isIssueModalVisible"
        :issues="issues"
        @save="saveIssue"
        @remove="removeIssue"
        @close="isIssueModalVisible = false"
      />
    </portal>
  </div>
</template>
